import React, {useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom"
import {Box, Grid, TextField, Stack, Button, Container, Paper} from "@mui/material";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import {LocationEndpoints} from "@/services/location/endpoints"
import GenericService from "@/services/GenericService";
import SnackbarUtil from "@/utils/SnackbarUtil";
import {object, TypeOf, z} from "zod";
import {useDispatch, useSelector} from "react-redux";
import {getOrganSites} from "@/store/slices/Site";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import ControlAutoComplete from "@/components/hook-form/ControlAutoComplete";
import {CancelButton, SavingButton} from "@/components/data-grid/CustomButton";
import WebURL from "@/urls";
import { useTranslation } from "react-i18next";

const ZoneView: React.FC = () => {
  const { t } = useTranslation();
  const zoneValidate = object({
    name: z.string({ required_error: t("common.This field is required!") }).min(1, t("common.This field is required!")),
    site: z.object({
      id: z.string(),
      name: z.string(),
    }, { required_error: t("common.This field is required!"), invalid_type_error: t("common.This field is required!")} ),
  })
  type zoneInput = TypeOf<typeof zoneValidate>;

  const location = useLocation()
  const state = location.state as { id: string }
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState<boolean>(false)
  const sites: {id: string, name: string, address: Record<string, any>}[] = useSelector((state: any) => state.site.sites);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch<any>(getOrganSites());
  }, [])

  const zoneForm = useForm<zoneInput>({
    resolver: zodResolver(zoneValidate),
    defaultValues: {
      site: {id: '', name: ''}
    }
  });
  useEffect(() => {
    GenericService.retrieve(LocationEndpoints.ZONE_URI, state.id || "").then((res) => {
      const zone = res.data
      zoneForm.reset({
        name: zone.name,
        site: {id: zone.site, name: zone.site_name}
      })

    })
  }, [])

  const onSave = async () => {
    setSaving(true)
    try {
      const values = zoneForm.getValues()
      const newZone = {
        name: values.name,
        siteId: values.site.id,
      }
      const response = await GenericService.update(LocationEndpoints.ZONE_URI,state.id || '', newZone)
      if (response.status < 400)
        SnackbarUtil.success(t('common.Zone updated successfully.'))
      else
        SnackbarUtil.error(response.message)
      navigate(-1)
    } catch (e) {
      console.log("error===", e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <CustomBreadcrumbs record={[
        { path: WebURL.LOCATION_ZONE, label: t("common.Zone") },
        {label: t("common.Edit Zone")},
      ]} />
      <Paper sx={{margin: '20px 0', padding: '20px'}}>
        <Grid container spacing={2} component="form">
          <Grid item xs={5} >
            <img width={"100%"} src={'/images/location-zone-view-rectangle.png'}/>
          </Grid>
          <Grid item xs={7}>
            <ControlledTextFiled required fullWidth label={t("common.Zone Name")} name={"name"} hookForm={zoneForm} />
            <ControlAutoComplete required label={t("common.Zone belongs to a Site")} name={"site"} form={zoneForm} options={sites}
            />
          </Grid>
        </Grid>
      </Paper>
      <Grid style={{'padding':'30px'}}>
        <Stack spacing={3} direction={"row"} justifyContent="flex-end" >
          <CancelButton/>
          <SavingButton loading={saving} onClick={zoneForm.handleSubmit(onSave)}/>
        </Stack>
      </Grid>
    </>
  )
}

export default ZoneView;
